import {
  SymbolSpecDto,
  TakePositionsResultDto,
  TerminalOrderDto,
  TerminalPositionDto,
} from '@/service/positions/terminal-position-types';
import client, { routes } from '@/utils/axios';
import { isAxiosError } from 'axios';

/// Entre deux lectures du verdict d'une clôture confiée au terminal de secours.
const RESCUE_POLL_MS = 5_000;
/// Le serveur rend son verdict en moins de dix minutes.
const RESCUE_GIVE_UP_MS = 12 * 60_000;

export class TerminalPositionService {
  static async takePositions({
    positions,
  }: {
    positions: TerminalPositionDto[];
  }): Promise<TakePositionsResultDto> {
    const res = await client.post<TakePositionsResultDto>(
      routes.positions.takePositions,
      positions,
    );
    return res.data;
  }

  /// Où en sont les ordres déposés : le backend tranche dès que l'exécuteur
  /// accuse réception.
  static async getOrders(ids: string[]): Promise<TerminalOrderDto[]> {
    if (!ids.length) return [];
    const res = await client.get<TerminalOrderDto[]>(routes.positions.orders, {
      params: { ids: ids.join(',') },
    });
    return res.data;
  }

  static async getRecentOrders(limit = 8): Promise<TerminalOrderDto[]> {
    const res = await client.get<TerminalOrderDto[]>(
      routes.positions.recentOrders,
      { params: { limit } },
    );
    return res.data;
  }

  /// Bornes de volume et tick du symbole chez le broker de ce compte.
  static async getSymbolSpec({
    accountId,
    symbol,
  }: {
    accountId: string;
    symbol: string;
  }): Promise<SymbolSpecDto> {
    const res = await client.get<SymbolSpecDto>(routes.positions.symbolSpec, {
      params: { accountId, symbol },
    });
    return res.data;
  }

  /// Quand le compte ne ferme pas lui-même, le serveur confie la clôture à un
  /// terminal de secours et répond `pending` : on prévient, puis on attend
  /// son verdict. Un échec arrive en 400, avec son motif ; une coupure réseau
  /// n'en est pas un, le secours continue et on relit.
  private static async settle(
    close: { accountId: string; posId: number },
    pending: boolean | undefined,
    onRescue?: VoidFunction,
  ): Promise<void> {
    if (!pending) return;
    onRescue?.();
    const deadline = Date.now() + RESCUE_GIVE_UP_MS;
    let failure: unknown = new Error('close status timeout');
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, RESCUE_POLL_MS));
      try {
        const { data } = await client.get<{ pending: boolean }>(
          routes.positions.closeStatus,
          { params: close },
        );
        if (!data.pending) return;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 400) throw error;
        failure = error;
      }
    }
    throw failure;
  }

  static async closePosition(
    { accountId, posId }: { accountId: string; posId: number },
    onRescue?: VoidFunction,
  ): Promise<void> {
    // `rescue` : cet écran sait attendre le verdict d'un terminal de secours.
    const { data } = await client.post<{ pending?: boolean }>(
      routes.positions.close,
      { accountId, posId, rescue: true },
    );
    await TerminalPositionService.settle(
      { accountId, posId },
      data?.pending,
      onRescue,
    );
  }

  static async partialClosePosition(
    {
      accountId,
      posId,
      volume,
    }: {
      accountId: string;
      posId: number;
      volume: number;
    },
    onRescue?: VoidFunction,
  ): Promise<void> {
    const { data } = await client.post<{ pending?: boolean }>(
      routes.positions.partialClose,
      { accountId, posId, volume, rescue: true },
    );
    await TerminalPositionService.settle(
      { accountId, posId },
      data?.pending,
      onRescue,
    );
  }

  /// Stop ou objectif d'une position ouverte ; un niveau absent reste celui
  /// du broker.
  static async modifyPosition({
    accountId,
    posId,
    sl,
    tp,
  }: {
    accountId: string;
    posId: number;
    sl?: number;
    tp?: number;
  }): Promise<void> {
    await client.post(routes.positions.modifyPosition, {
      accountId,
      posId,
      sl,
      tp,
    });
  }

  static async cancelOrder({
    accountId,
    orderId,
  }: {
    accountId: string;
    orderId: number;
  }): Promise<void> {
    await client.post(routes.positions.cancelOrder, { accountId, orderId });
  }

  static async modifyOrder({
    accountId,
    orderId,
    price,
    sl,
    tp,
  }: {
    accountId: string;
    orderId: number;
    price?: number;
    sl?: number;
    tp?: number;
  }): Promise<void> {
    await client.post(routes.positions.modifyOrder, {
      accountId,
      orderId,
      price,
      sl,
      tp,
    });
  }
}
